/// <reference path="../../../types/axios-fix.d.ts" />
import { AxiosError, AxiosResponse } from 'axios';
import { ApiError, ErrorCode, isApiError } from '../error-handling';
import connectionStatusService from '../connectionStatusService';
import { logError } from '../../utils/error-handler';

/**
 * SHARP API Response Standard interface
 */
export interface ApiResponse<T> {
  data: T;
  message?: string;
  metadata?: {
    pagination?: {
      totalCount: number;
      pageSize: number;
      currentPage: number;
      totalPages: number;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
    [key: string]: any;
  };
  timestamp?: string;
  requestId?: string;
  _links?: Record<string, { href: string; method?: string; title?: string }>;
}

/**
 * API error response interface
 */
export interface ApiErrorResponse {
  error: {
    code: string;
    message: string;
    details?: any;
    requestId?: string;
    serviceName?: string;
  };
  status: number;
}

/**
 * Enhanced Error Handler Service
 * 
 * Centralizes error handling and response transformation for all API calls.
 * Based on patterns from the existing monolithic API service.
 * Provides consistent error formatting, logging, and response data extraction.
 */
export class ErrorHandlerService {
  /**
   * Extract error information from various error types
   */
  extractErrorMessage(error: unknown): ApiError {
    // Handle AxiosError specifically
    if (this.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      
      // Check for structured error response
      if (axiosError.response?.data) {
        const responseData = axiosError.response.data as any;
        
        // SHARP API error format
        if (responseData.error) {
          return {
            code: responseData.error.code || 'UNKNOWN_ERROR',
            message: responseData.error.message || 'An error occurred',
            details: responseData.error.details,
            requestId: responseData.error.requestId,
            serviceName: responseData.error.serviceName,
            status: axiosError.response.status
          };
        }
        
        // Direct message format
        if (responseData.message) {
          return {
            code: responseData.code || 'UNKNOWN_ERROR',
            message: responseData.message,
            details: responseData.details,
            status: axiosError.response.status
          };
        }
        
        // Fallback to generic error
        return {
          code: 'API_ERROR',
          message: `HTTP ${axiosError.response.status}: ${axiosError.response.statusText}`,
          details: responseData,
          status: axiosError.response.status
        };
      }
      
      // Network or request error
      return {
        code: axiosError.code || 'NETWORK_ERROR',
        message: axiosError.message || 'Network error occurred',
        status: 0
      };
    }

    // Handle already formatted ApiError
    if (isApiError(error)) {
      return error as ApiError;
    }

    // Handle generic errors
    if (error instanceof Error) {
      return {
        code: 'UNKNOWN_ERROR',
        message: error.message,
        status: 500
      };
    }
    
    // Handle unknown error types
    return {
      code: 'UNKNOWN_ERROR',
      message: 'An unknown error occurred',
      status: 500
    };
  }

  /**
   * Type guard for AxiosError
   */
  private isAxiosError(error: any): error is AxiosError {
    return error && typeof error === 'object' && error.isAxiosError === true;
  }

  /**
   * Check if error is a network error
   */
  isNetworkError(error: unknown): boolean {
    if (this.isAxiosError(error)) {
      return !error.response && (error.code === 'ECONNABORTED' || error.message.includes('Network Error'));
    }
    return false;
  }

  /**
   * Check if error is a server error (5xx)
   */
  isServerError(error: unknown): boolean {
    if (this.isAxiosError(error)) {
      return error.response?.status !== undefined && error.response.status >= 500;
    }
    return false;
  }

  /**
   * Check if error is an authentication error (401/403)
   */
  isAuthError(error: unknown): boolean {
    if (this.isAxiosError(error)) {
      return error.response?.status === 401 || error.response?.status === 403;
    }
    return false;
  }

  /**
   * Get error code from error object
   */
  getErrorCode(error: unknown): string | undefined {
    const apiError = this.extractErrorMessage(error);
    return apiError.code;
  }

  /**
   * Get HTTP status from error object
   */
  getErrorStatus(error: unknown): number | undefined {
    const apiError = this.extractErrorMessage(error);
    return apiError.status;
  }

  /**
   * Extract data from SHARP API response format
   * Handles both single data responses and paginated responses
   */
  extractResponseData<T>(response: AxiosResponse): T {
    // Check if this is our special 401 handled response
    if (response.status === 401 && response.data === null) {
      console.log('ErrorHandler: Detected 401 handled response, returning null');
      return null as T;
    }
    
    // Handle SHARP API Response Standard format
    if (response.data && typeof response.data === 'object') {
      // If response.data has a 'data' property, extract it
      if ('data' in response.data) {
        return response.data.data as T;
      }
      // Otherwise return the data directly
      return response.data as T;
    }
    return response.data as T;
  }

  /**
   * Extract response with metadata (for paginated responses)
   */
  extractResponseWithMetadata<T>(response: AxiosResponse): ApiResponse<T> {
    // Check if this is our special 401 handled response
    if (response.status === 401 && response.data === null) {
      console.log('ErrorHandler: Detected 401 handled response in metadata extraction, returning null');
      return {
        data: null as T,
        timestamp: new Date().toISOString()
      };
    }
    
    if (response.data && typeof response.data === 'object') {
      return response.data as ApiResponse<T>;
    }
    
    // Fallback: wrap raw data in API response format
    return {
      data: response.data as T,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Log error for debugging (can be extended to send to logging service)
   */
  logError(error: ApiError, context?: string): void {
    const logMessage = `API Error${context ? ` (${context})` : ''}`;
    
    // Use the safe logError function to prevent webpack overlay issues
    logError(logMessage, error);
    
    // Check for network errors and update connection status
    if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error') || error.message?.includes('Unable to connect to the server')) {
      connectionStatusService.setStatus('disconnected');
    }
  }

  /**
   * Handle and format error consistently
   */
  handleError(error: unknown, context?: string): ApiError {
    const formattedError = this.extractErrorMessage(error);
    this.logError(formattedError, context);
    return formattedError;
  }

  /**
   * Create a standardized error for common scenarios
   */
  createError(code: ErrorCode, message: string, details?: any): ApiError {
    return {
      code,
      message,
      details,
      status: this.getStatusForErrorCode(code)
    };
  }

  /**
   * Map error codes to HTTP status codes
   */
  private getStatusForErrorCode(code: ErrorCode): number {
    switch (code) {
      case ErrorCode.UNAUTHORIZED:
      case ErrorCode.INVALID_CREDENTIALS:
      case ErrorCode.TOKEN_EXPIRED:
      case ErrorCode.TOKEN_INVALID:
        return 401;
      case ErrorCode.FORBIDDEN:
      case ErrorCode.INSUFFICIENT_PERMISSIONS:
        return 403;
      case ErrorCode.RESOURCE_NOT_FOUND:
        return 404;
      case ErrorCode.RESOURCE_ALREADY_EXISTS:
        return 409;
      default:
        return 500;
    }
  }
}

// Export singleton instance
export const errorHandlerService = new ErrorHandlerService();